import type React from 'react'
import AssetIcon from './AssetIcon'
import CountryFlag from './CountryFlag'
import { countryLabel, formatMoneyCompact } from './dashboardFormat'

export interface LeaderboardRow {
  /** Leading badge: an SVG flag, an icon, etc. (rendered as-is, not an emoji string). */
  badge: React.ReactNode
  label: string
  /** Right-aligned primary figure already formatted for display. */
  value: string
  /** 0..1 share used to size the bar fill. */
  fraction: number
}

interface LeaderboardProps {
  title: string
  rows: LeaderboardRow[]
  accentClass: string
  emptyLabel: string
}

const Leaderboard: React.FC<LeaderboardProps> = ({
  title,
  rows,
  accentClass,
  emptyLabel,
}) => (
  <div className="flex h-full min-h-0 flex-col">
    <h3 className="mb-3 shrink-0 text-xs font-semibold uppercase tracking-widest text-white/40">
      {title}
    </h3>
    {rows.length === 0 ? (
      <p className="text-sm text-white/30">{emptyLabel}</p>
    ) : (
      <ul className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
        {rows.map((row, idx) => (
          <li key={`${row.label}-${idx}`} className="relative">
            <div className="relative flex items-center gap-3 overflow-hidden rounded-lg bg-white/[0.03] px-3 py-2">
              <div
                className={`absolute inset-y-0 left-0 ${accentClass} opacity-15`}
                style={{ width: `${Math.max(row.fraction * 100, 4)}%` }}
                aria-hidden
              />
              <span className="relative z-10 flex w-7 items-center justify-center text-lg">
                {row.badge}
              </span>
              <span className="relative z-10 flex-1 truncate text-sm font-medium text-white/90 xl:text-base">
                {row.label}
              </span>
              <span className="relative z-10 text-sm font-semibold tabular-nums text-white xl:text-base">
                {row.value}
              </span>
            </div>
          </li>
        ))}
      </ul>
    )}
  </div>
)

// Helper builders so the screen stays declarative.
export function countryRows(
  data: { country: string; customerCount: number }[]
): LeaderboardRow[] {
  const max = Math.max(1, ...data.map(d => d.customerCount))
  return data.map(d => ({
    badge: <CountryFlag country={d.country} className="h-4 w-6" />,
    label: countryLabel(d.country),
    value: String(d.customerCount),
    fraction: d.customerCount / max,
  }))
}

export function assetRows(
  data: { isoCode: string; name: string; volume: number; iconUrl: string }[],
  currency: string
): LeaderboardRow[] {
  const max = Math.max(1, ...data.map(d => d.volume))
  return data.map(d => ({
    badge: <AssetIcon iconUrl={d.iconUrl} alt={d.isoCode} className="h-5 w-5" />,
    label: `${d.name} (${d.isoCode})`,
    value: formatMoneyCompact(d.volume, currency),
    fraction: d.volume / max,
  }))
}

export default Leaderboard
